// src/components/modals/NewCustomerModal.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone } from 'lucide-react';
import Modal from '../ui/Modal';
import PrimaryButton from '../ui/PrimaryButton';
import CountryPhoneInput from '../ui/CountryPhoneInput';

import { useClients } from '@/contexts/ClientsContext';
import { toast } from 'sonner';
import {
  validateEmail,
  validatePhone,
  validateName,
} from '@/lib/validator';

interface NewCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewCustomerModal: React.FC<NewCustomerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { createClient } = useClients();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  const canSubmit =
    formData.firstName.trim().length > 0 &&
    formData.phone.trim().length > 0 &&
    !isSaving;

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    });
  };

  const handleSubmit = async () => {
    if (isSaving) return;

    if (!formData.firstName.trim()) {
      toast.error(t('customers.modal.toast.name_required', 'Il nome è obbligatorio'));
      return;
    }

    if (!formData.phone.trim()) {
      toast.error(t('customers.modal.toast.phone_required', 'Il telefono è obbligatorio'));
      return;
    }

    // Validazioni come in ClientsForm
    const firstNameValidation = validateName(formData.firstName, 'Nome');
    if (!firstNameValidation.isValid) {
      toast.error(firstNameValidation.message);
      return;
    }

    if (formData.lastName.trim()) {
      const lastNameValidation = validateName(formData.lastName, 'Cognome');
      if (!lastNameValidation.isValid) {
        toast.error(lastNameValidation.message);
        return;
      }
    }

    if (formData.email) {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        toast.error(emailValidation.message);
        return;
      }
    }

    const phoneValidation = validatePhone(formData.phone);
    if (!phoneValidation.isValid) {
      toast.error(phoneValidation.message);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim() || '',
        phone: formData.phone.trim() || '',
      };

      const res = await createClient(payload);

      if (!res || !res.success) {
        toast.error(
          res?.error || "Errore nella creazione del cliente"
        );
        setIsSaving(false);
        return;
      }

      toast.success('Cliente creato con successo');
      resetForm();
      setIsSaving(false);
      onClose();
    } catch (err: any) {
      console.error('Errore createClient:', err);
      toast.error(
        err?.message || "Errore nella creazione del cliente"
      );
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      headerContent={
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('customers.modal.create_title')}
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {t('customers.modal.create_subtitle')}
          </p>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              {t('customers.modal.name')} *
            </label>
            <input
              type="text"
              placeholder="Mario"
              value={formData.firstName}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))
              }
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium text-gray-900"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              {t('customers.modal.surname')} (Opzionale)
            </label>
            <input
              type="text"
              placeholder="Rossi"
              value={formData.lastName}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  lastName: e.target.value,
                }))
              }
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium text-gray-900"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Phone size={14} /> {t('customers.modal.phone')} *
          </label>
          <div className="relative">
            <CountryPhoneInput
              value={formData.phone}
              onChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  phone: val,
                }))
              }
              placeholder="333 123 4567"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
            <Mail size={14} /> {t('customers.modal.email_optional')}
          </label>
          <input
            type="email"
            placeholder="mario.rossi@example.com"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                email: e.target.value,
              }))
            }
            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium text-gray-900"
          />
        </div>

        <div className="pt-6 mt-2 border-t border-gray-100 flex gap-4">
          <button
            onClick={handleClose}
            className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <PrimaryButton
            label={t('customers.modal.create_btn')}
            className="flex-1 justify-center"
            onClick={handleSubmit}
            disabled={!canSubmit}
          />
        </div>
      </div>
    </Modal>
  );
};

export default NewCustomerModal;